/**
 * OSEListsAggregator — Renders every OS&E / inventory list across every
 * department in a single flat directory.
 *
 * Used inside `WorkspaceDialog` when the active node is `ose-lists` so
 * users see ALL departments' lists at one place instead of having to
 * drill into each branch. Click any card → opens that list inside the
 * shared `InventoryTable` dialog scoped to its real node_id.
 *
 * Per-card Archive (amber ▼) + Delete (red ×) buttons let any signed-in
 * user remove a list directly from the aggregator, mirroring the
 * controls available everywhere else lists are surfaced (InventoryTable
 * saved-list tabs, WorkspaceSavedFiles). Archive uses the soft-hide
 * endpoint (editor+); delete uses the hard-delete endpoint (any signed
 * in user, default list excluded).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, FolderOpen, Package } from 'lucide-react';
import usePermissions from '../lib/usePermissions';
import { isAdminLike } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OSEListsAggregator({ onOpenList }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const { role, loading: permLoading } = usePermissions();
  const canArchive = !permLoading && (role === 'editor' || isAdminLike(role));
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const headers = adminToken ? { Authorization: `Bearer ${adminToken}` } : {};
    axios.get(`${API}/workspace/inventory-lists/all`, { headers })
      .then(({ data }) => { if (!cancelled) setLists(data || []); })
      .catch(() => { if (!cancelled) setLists([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminToken]);

  const archiveList = async (li) => {
    if (!adminToken || busyId) return;
    if (li.id === 'default') {
      alert('The default list of a department cannot be archived.');
      return;
    }
    if (!window.confirm(`Archive "${li.name}"? Admins can restore it later from Admin → Archive.`)) return;
    try {
      setBusyId(li.id);
      await axios.put(`${API}/admin/archive/inventory_list/${li.id}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLists((ls) => ls.filter((x) => x.id !== li.id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to archive');
    } finally {
      setBusyId(null);
    }
  };

  const deleteList = async (li) => {
    if (!adminToken || busyId) return;
    if (li.id === 'default') {
      alert('The default list of a department cannot be deleted.');
      return;
    }
    if (!window.confirm(`Permanently delete "${li.name}" and re-bucket its items to the default list? Pick Archive instead if you might want it back.`)) return;
    try {
      setBusyId(li.id);
      await axios.delete(`${API}/admin/workspace/inventory-lists/${li.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setLists((ls) => ls.filter((x) => x.id !== li.id));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="py-10 flex items-center justify-center text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading every OS&E list across the property…
      </div>
    );
  }

  if (lists.length === 0) {
    return (
      <div className="py-10 text-center text-sm" style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}>
        No OS&E lists have been created yet. Head into any department workspace and add one.
      </div>
    );
  }

  // Group by node_label so departments cluster naturally.
  const groups = {};
  for (const li of lists) {
    const key = li.node_label || 'Other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(li);
  }
  const groupNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-5" data-testid="ose-lists-aggregator">
      <div className="flex items-center gap-2">
        <FolderOpen className="w-4 h-4" style={{ color: '#003DA5' }} />
        <h3 className="text-xs uppercase tracking-[0.15em] font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
          Every OS&E list · {lists.length} across {groupNames.length} departments
        </h3>
      </div>
      {groupNames.map((groupName) => (
        <div key={groupName}>
          <p
            className="text-[10px] uppercase tracking-wider font-bold mb-2"
            style={{ color: '#5C6B6B', fontFamily: 'Manrope' }}
          >
            {groupName} <span className="opacity-60">· {groups[groupName].length}</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {groups[groupName].map((li) => {
              const isDefault = li.id === 'default';
              const isBusy = busyId === li.id;
              return (
                <div
                  key={li.id}
                  data-testid={`ose-aggregator-list-${li.id}`}
                  className="relative p-3 rounded-xl flex items-start gap-2 transition hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                  style={{ background: 'white', border: '1px solid #E2DBC8', fontFamily: 'Manrope', opacity: isBusy ? 0.5 : 1 }}
                  onClick={() => !isBusy && onOpenList?.(li)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isBusy) onOpenList?.(li); }}
                >
                  <Package className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#003DA5' }} />
                  <div className="min-w-0 flex-1 pr-12">
                    <p className="text-sm font-bold truncate" style={{ color: '#1A2E2E' }}>{li.name}</p>
                    <p className="text-[10px] mt-0.5" style={{ color: '#5C6B6B' }}>
                      {li.item_count} item{li.item_count === 1 ? '' : 's'}
                      {li.updated_at && <> · {new Date(li.updated_at).toLocaleDateString()}</>}
                    </p>
                  </div>
                  {adminToken && !isDefault && (
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      {canArchive && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); archiveList(li); }}
                          disabled={isBusy}
                          className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold transition hover:scale-110 disabled:opacity-50"
                          style={{ backgroundColor: '#FEF3C7', color: '#B45309' }}
                          title="Archive list — admins can restore it later from Admin → Archive"
                          data-testid={`ose-aggregator-archive-${li.id}`}
                        >▼</button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteList(li); }}
                        disabled={isBusy}
                        className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold transition hover:scale-110 disabled:opacity-50"
                        style={{ backgroundColor: '#FEE2E2', color: '#B91C1C' }}
                        title="Permanently delete list (items re-buckered into the default list)"
                        data-testid={`ose-aggregator-delete-${li.id}`}
                      >×</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
